import styled from '@emotion/styled'
import React, { useState } from 'react'
import { useQuery } from '../../../api/graphql'
import Loading from '../misc/Loading'
import {
  BUSINESS_CALENDAR_ANALYTICS_QUERY,
  BusinessCalendarAnalyticsResponse,
  EventDailyView,
  NetworkSite,
  TopEvent,
} from './BusinessCalendarQueries'
import NetworkFlowViz, { SocialNodeState } from './NetworkFlowViz'
import { Hugeicon } from '../../../icons/Hugeicon'

/* ── Types ── */

type Timeframe = 'last_7_days' | 'last_30_days' | 'last_90_days' | 'all_time'

const TIMEFRAME_OPTIONS: { value: Timeframe; label: string; description: string }[] = [
  { value: 'last_7_days', label: 'Last 7 Days', description: 'Last 7 days' },
  { value: 'last_30_days', label: 'Last 30 Days', description: 'Last 30 days' },
  { value: 'last_90_days', label: 'Last 90 Days', description: 'Last 90 days' },
  { value: 'all_time', label: 'All Time', description: 'All time' },
]

/* ── Styled Components ── */

const Section = styled.div``

const SectionHeader = styled.div`
  margin-bottom: 20px;
`

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #102638;
  margin: 0 0 6px;
`

const SectionDescription = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
`

const TimeframeSelector = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 20px;
`

const TimeframeButton = styled.button<{ active: boolean }>`
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 500;
  background: ${({ active }) => (active ? '#1e3a5f' : 'white')};
  border: 1px solid ${({ active }) => (active ? '#1e3a5f' : '#e5e7eb')};
  border-radius: 6px;
  color: ${({ active }) => (active ? 'white' : '#374151')};
  text-decoration: none;
  transition: all 0.15s ease;
  cursor: pointer;

  &:hover {
    border-color: #1e3a5f;
    color: ${({ active }) => (active ? 'white' : '#1e3a5f')};
  }
`

const EmptyStateCard = styled.div`
  background: #f9fafb;
  border: 1px dashed #d1d5db;
  border-radius: 12px;
  padding: 48px 32px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const EmptyStateIcon = styled.div`
  width: 48px;
  height: 48px;
  color: #9ca3af;
  margin-bottom: 16px;
`

const EmptyStateTitle = styled.p`
  font-size: 17px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 8px;
`

const EmptyStateDescription = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  line-height: 1.6;
  max-width: 420px;
`

const SubmitEventButton = styled.a`
  display: inline-block;
  margin-top: 16px;
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  color: white;
  font-size: 14px;
  font-weight: 600;
  padding: 10px 24px;
  border-radius: 8px;
  text-decoration: none;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
    color: white;
  }
`

const MetricGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 32px;
`

const MetricCard = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 24px;
`

const MetricValue = styled.div`
  font-size: 32px;
  font-weight: 800;
  color: #102638;
  line-height: 1;
  margin-bottom: 8px;
`

const MetricLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
`

const MetricDescription = styled.div`
  font-size: 12px;
  color: #9ca3af;
`

const SubTitle = styled.h4`
  font-size: 16px;
  font-weight: 700;
  color: #102638;
  margin: 0 0 16px;
`

const EventsSection = styled.div`
  margin-top: 40px;
`

const EventsSectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`

const TableLegend = styled.div`
  display: flex;
  gap: 16px;
`

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #6b7280;
`

const LegendDot = styled.div<{ color: string }>`
  width: 12px;
  height: 3px;
  border-radius: 2px;
  background: ${({ color }) => color};
`

const EventsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const EventCard = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px 20px;
  display: grid;
  grid-template-columns: 1fr 200px repeat(3, 90px);
  align-items: center;
  gap: 16px;
  transition: all 0.15s ease;

  &:hover {
    border-color: #d1d5db;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  }
`

const EventInfo = styled.div``

const EventTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #102638;
  line-height: 1.3;
`

const EventDate = styled.div`
  font-size: 12px;
  color: #9ca3af;
  margin-top: 4px;
`

const ChartContainer = styled.div`
  position: relative;
  cursor: crosshair;
`

const ChartTooltip = styled.div`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: #1e293b;
  color: white;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 11px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 10;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  margin-bottom: 8px;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: #1e293b;
  }
`

const TooltipRow = styled.div<{ color: string }>`
  display: flex;
  align-items: center;
  gap: 6px;

  &::before {
    content: '';
    width: 8px;
    height: 3px;
    border-radius: 1px;
    background: ${({ color }) => color};
  }
`

const MetricCell = styled.div<{ align?: string }>`
  text-align: ${({ align }) => align || 'right'};
`

const CellLabel = styled.div`
  font-size: 10px;
  font-weight: 500;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin-bottom: 2px;
`

const CellValue = styled.div<{ color?: string; large?: boolean }>`
  font-size: ${({ large }) => (large ? '18px' : '16px')};
  font-weight: ${({ large }) => (large ? '700' : '600')};
  color: ${({ color }) => color || '#102638'};
`

/* ── Helper Functions ── */

function formatNumber(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`
  return n.toString()
}

function formatDate(iso: string, timeZone?: string | null): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      ...(timeZone ? { timeZone } : {}),
    })
  } catch {
    return iso
  }
}

/* ── Interactive Chart Component ── */

type InteractiveChartProps = {
  data: EventDailyView[]
  width?: number
  height?: number
}

const InteractiveChart: React.FC<InteractiveChartProps> = ({ data, width = 180, height = 48 }) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)

  if (data.length < 2) return null

  const websiteValues = data.map(d => d.siteViews)
  const networkValues = data.map(d => d.networkViews)
  const allValues = [...websiteValues, ...networkValues]
  const maxValue = Math.max(...allValues, 1)
  const minValue = 0
  const range = maxValue - minValue || 1

  const padding = 4
  const chartWidth = width - padding * 2
  const chartHeight = height - padding * 2

  const getX = (i: number) => padding + (i / (data.length - 1)) * chartWidth
  const getY = (value: number) => padding + chartHeight - ((value - minValue) / range) * chartHeight

  const createPath = (values: number[]) => {
    return values.map((value, i) => `${i === 0 ? 'M' : 'L'} ${getX(i)} ${getY(value)}`).join(' ')
  }

  const createAreaPath = (values: number[]) => {
    const linePath = values.map((value, i) => `${i === 0 ? 'M' : 'L'} ${getX(i)} ${getY(value)}`).join(' ')
    return `${linePath} L ${getX(values.length - 1)} ${height - padding} L ${getX(0)} ${height - padding} Z`
  }

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const x = e.clientX - rect.left - padding
    const index = Math.round((x / chartWidth) * (data.length - 1))
    if (index >= 0 && index < data.length) {
      setHoveredIndex(index)
    }
  }

  const hoveredData = hoveredIndex !== null ? data[hoveredIndex] : null

  return (
    <ChartContainer onMouseLeave={() => setHoveredIndex(null)}>
      {hoveredData && (
        <ChartTooltip>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            {new Date(hoveredData.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </div>
          <TooltipRow color="#1e3a5f">Your site: {hoveredData.siteViews} impressions</TooltipRow>
          <TooltipRow color="#93c5fd">Network: {hoveredData.networkViews} impressions</TooltipRow>
        </ChartTooltip>
      )}
      <svg width={width} height={height} style={{ display: 'block' }} onMouseMove={handleMouseMove}>
        {/* Network area fill */}
        <path d={createAreaPath(networkValues)} fill="url(#networkGradient)" opacity="0.3" />
        {/* Website area fill */}
        <path d={createAreaPath(websiteValues)} fill="url(#websiteGradient)" opacity="0.2" />
        {/* Network line */}
        <path d={createPath(networkValues)} fill="none" stroke="#93c5fd" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {/* Website line */}
        <path d={createPath(websiteValues)} fill="none" stroke="#1e3a5f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />

        {/* Hover indicator */}
        {hoveredIndex !== null && (
          <>
            <line x1={getX(hoveredIndex)} y1={padding} x2={getX(hoveredIndex)} y2={height - padding} stroke="#d1d5db" strokeWidth="1" strokeDasharray="2,2" />
            <circle cx={getX(hoveredIndex)} cy={getY(websiteValues[hoveredIndex])} r="4" fill="#1e3a5f" stroke="white" strokeWidth="2" />
            <circle cx={getX(hoveredIndex)} cy={getY(networkValues[hoveredIndex])} r="4" fill="#93c5fd" stroke="white" strokeWidth="2" />
          </>
        )}

        {/* Gradients */}
        <defs>
          <linearGradient id="websiteGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1e3a5f" />
            <stop offset="100%" stopColor="#1e3a5f" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="networkGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#93c5fd" />
            <stop offset="100%" stopColor="#93c5fd" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>
    </ChartContainer>
  )
}

/* ── Main Component ── */

type Props = {
  organizationId: string
  networkSites?: NetworkSite[]
  facebookState?: SocialNodeState
  instagramState?: SocialNodeState
  onNavigateToSocial?: () => void
  onNavigateToBilling?: () => void
}

// Convert timeframe to date range
function getDateRange(timeframe: Timeframe): { startDate: string; endDate: string } {
  const today = new Date()
  const endDate = today.toISOString().split('T')[0]

  let startDate: string
  switch (timeframe) {
    case 'last_7_days':
      startDate = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      break
    case 'last_30_days':
      startDate = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      break
    case 'last_90_days':
      startDate = new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      break
    case 'all_time':
      startDate = new Date(today.getTime() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      break
  }

  return { startDate, endDate }
}

const AnalyticsDashboard: React.FC<Props> = ({
  organizationId,
  networkSites = [],
  facebookState = 'locked',
  instagramState = 'locked',
  onNavigateToSocial,
  onNavigateToBilling,
}) => {
  const [timeframe, setTimeframe] = useState<Timeframe>('last_30_days')
  const { startDate, endDate } = getDateRange(timeframe)

  const { data, loading } = useQuery<BusinessCalendarAnalyticsResponse>(BUSINESS_CALENDAR_ANALYTICS_QUERY, {
    variables: { organizationId, startDate, endDate },
  })

  if (loading) {
    return <Loading className="isCentered isDark" />
  }

  const analytics = data?.businessCalendarAnalytics
  const hasRealData =
    analytics && (analytics.summary.siteImpressions > 0 || analytics.summary.networkImpressions > 0 || analytics.topEvents.length > 0)

  // Always show network flow viz, then either real metrics or an empty state
  if (!hasRealData) {
    return (
      <Section>
        <NetworkFlowViz
          networkSites={networkSites}
          facebookState={facebookState}
          instagramState={instagramState}
          onNavigateToSocial={onNavigateToSocial}
          onNavigateToBilling={onNavigateToBilling}
        />

        <EmptyStateCard>
          <EmptyStateIcon>
            <Hugeicon name="chart-column" size={48} />
          </EmptyStateIcon>
          <EmptyStateTitle>Your calendar is ready!</EmptyStateTitle>
          <EmptyStateDescription>
            Submit an event to start seeing analytics here. Once your calendar gets views, you&apos;ll see your
            reach across your site and {networkSites.length || 'partner'} community calendars.
          </EmptyStateDescription>
          <SubmitEventButton href="/dash/event/add">Submit an Event</SubmitEventButton>
        </EmptyStateCard>
      </Section>
    )
  }

  const { topEvents, summary } = analytics
  const timeframeDescription = TIMEFRAME_OPTIONS.find(t => t.value === timeframe)?.description || 'Last 30 days'

  const totalYourSiteImpressions = summary.siteImpressions
  const totalNetworkImpressions = summary.networkImpressions
  const totalYourSiteClicks = summary.siteClicks
  const totalNetworkClicks = summary.networkClicks
  const totalYourSitePageViews = summary.sitePageViews
  const totalNetworkPageViews = summary.networkPageViews

  // Calculate network multiplier (how many more impressions they get from network)
  const networkMultiplier = totalYourSiteImpressions > 0 ? (totalYourSiteImpressions + totalNetworkImpressions) / totalYourSiteImpressions : 1
  const multiplierDisplay = networkMultiplier >= 10 ? Math.round(networkMultiplier) : networkMultiplier.toFixed(1)
  const partnerSitesCount = networkSites.length

  return (
    <Section>
      <NetworkFlowViz
          networkSites={networkSites}
          facebookState={facebookState}
          instagramState={instagramState}
          onNavigateToSocial={onNavigateToSocial}
          onNavigateToBilling={onNavigateToBilling}
        />

      <SectionHeader>
        <SectionTitle>Your Network Reach</SectionTitle>
        <SectionDescription>{timeframeDescription} of activity across your calendar and the CivicLift network.</SectionDescription>
      </SectionHeader>

      <TimeframeSelector>
        {TIMEFRAME_OPTIONS.map(option => (
          <TimeframeButton key={option.value} active={timeframe === option.value} onClick={() => setTimeframe(option.value)}>
            {option.label}
          </TimeframeButton>
        ))}
      </TimeframeSelector>

      <MetricGrid>
        <MetricCard>
          <MetricValue>{formatNumber(totalYourSiteImpressions)}</MetricValue>
          <MetricLabel>Impressions on Your Site</MetricLabel>
          <MetricDescription>Times your events appeared on your embedded calendar</MetricDescription>
        </MetricCard>
        <MetricCard>
          <MetricValue>{formatNumber(totalNetworkImpressions)}</MetricValue>
          <MetricLabel>Impressions on Network</MetricLabel>
          <MetricDescription>Times your events appeared on partner community sites</MetricDescription>
        </MetricCard>
        <MetricCard>
          <MetricValue>{multiplierDisplay}x</MetricValue>
          <MetricLabel>Impressions Multiplier</MetricLabel>
          <MetricDescription>More reach than your site alone</MetricDescription>
        </MetricCard>
        <MetricCard>
          <MetricValue>{partnerSitesCount}</MetricValue>
          <MetricLabel>Partner Sites</MetricLabel>
          <MetricDescription>Community calendars showing your events</MetricDescription>
        </MetricCard>
      </MetricGrid>

      <SectionHeader>
        <SectionTitle>Page Views</SectionTitle>
        <SectionDescription>How many times people visited a calendar page showing your events.</SectionDescription>
      </SectionHeader>

      <MetricGrid>
        <MetricCard>
          <MetricValue>{formatNumber(totalYourSitePageViews)}</MetricValue>
          <MetricLabel>Page Views on Your Site</MetricLabel>
          <MetricDescription>Visits to your embedded calendar</MetricDescription>
        </MetricCard>
        <MetricCard>
          <MetricValue>{formatNumber(totalNetworkPageViews)}</MetricValue>
          <MetricLabel>Page Views on Network</MetricLabel>
          <MetricDescription>Visits to partner calendars showing your events</MetricDescription>
        </MetricCard>
      </MetricGrid>

      <SectionHeader>
        <SectionTitle>Detail Page Views</SectionTitle>
        <SectionDescription>How many times people clicked through to view full event details.</SectionDescription>
      </SectionHeader>

      <MetricGrid>
        <MetricCard>
          <MetricValue>{formatNumber(totalYourSiteClicks)}</MetricValue>
          <MetricLabel>Detail Views on Your Site</MetricLabel>
          <MetricDescription>Event detail clicks on your embedded calendar</MetricDescription>
        </MetricCard>
        <MetricCard>
          <MetricValue>{formatNumber(totalNetworkClicks)}</MetricValue>
          <MetricLabel>Detail Views on Network</MetricLabel>
          <MetricDescription>Event detail clicks on partner community sites</MetricDescription>
        </MetricCard>
      </MetricGrid>

      {topEvents.length > 0 && (
        <EventsSection>
          <EventsSectionHeader>
            <SubTitle style={{ margin: 0 }}>Top Events by Impressions</SubTitle>
            <TableLegend>
              <LegendItem>
                <LegendDot color="#1e3a5f" />
                Your Site
              </LegendItem>
              <LegendItem>
                <LegendDot color="#93c5fd" />
                Network
              </LegendItem>
            </TableLegend>
          </EventsSectionHeader>

          <EventsList>
            {topEvents.map((event: TopEvent) => (
              <EventCard key={event.eventId}>
                <EventInfo>
                  <EventTitle>{event.title}</EventTitle>
                  {event.startAt && <EventDate>{formatDate(event.startAt, event.timeZone)}</EventDate>}
                </EventInfo>

                <InteractiveChart data={event.dailyViews} />

                <MetricCell>
                  <CellLabel>Site Impressions</CellLabel>
                  <CellValue color="#1e3a5f">{formatNumber(event.siteViews)}</CellValue>
                </MetricCell>

                <MetricCell>
                  <CellLabel>Network Impressions</CellLabel>
                  <CellValue color="#3b82f6">{formatNumber(event.networkViews)}</CellValue>
                </MetricCell>

                <MetricCell>
                  <CellLabel>Total Impressions</CellLabel>
                  <CellValue large>{formatNumber(event.siteViews + event.networkViews)}</CellValue>
                </MetricCell>
              </EventCard>
            ))}
          </EventsList>
        </EventsSection>
      )}
    </Section>
  )
}

export default AnalyticsDashboard
